import { useMemo, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useGetTargetareFirmeCriteriu } from "@/hooks/api/query/useGetTargetareFirmeCriteriu";
import { useGetTargetareTinte } from "@/hooks/api/query/useGetTargetareTinte";
import type {
  TargetareCriteriuFirme,
  TargetareUniversRand,
} from "@/types/targetare";
import { descriereCriteriu } from "@/utils/targetareConfig";
import { formatNumar } from "@/utils/targetareFormatting";
import type { DefinitieRaport } from "@/utils/targetareRaport";
import { definitieRaportFirme } from "@/utils/targetareRaportFirme";
import { FisaFirmaDialog } from "./FisaFirmaDialog";
import { celulaFirma } from "./raport/CelulaFirma";
import { RaportInteractiv } from "./raport/RaportInteractiv";
import { AdaugaTinteDialog } from "./tinte/AdaugaTinteDialog";

export type FirmeCriteriuDeschis = {
  criteriu: TargetareCriteriuFirme;
  titlu?: string;
};

type FirmeCriteriuDialogProps = {
  deschis: FirmeCriteriuDeschis | null;
  onClose: () => void;
};

const definitieModal: DefinitieRaport<TargetareUniversRand> = {
  ...definitieRaportFirme,
  cheie: "targetare.rapoarte.modal",
  numeFisier: "firme-criteriu",
  mesajGol: "Nicio firmă nu corespunde criteriului.",
};

export function FirmeCriteriuDialog({
  deschis,
  onClose,
}: FirmeCriteriuDialogProps) {
  const [doarScop, setDoarScop] = useState(true);
  const [cuiSelectat, setCuiSelectat] = useState<string | null>(null);
  const [tinteDeAdaugat, setTinteDeAdaugat] = useState<string[] | null>(null);
  const tinte = useGetTargetareTinte();
  const criteriu = deschis?.criteriu ?? null;
  const firme = useGetTargetareFirmeCriteriu(criteriu, doarScop);
  const randuri = useMemo(() => firme.data?.randuri ?? [], [firme.data]);
  const titlu = deschis
    ? (deschis.titlu ?? descriereCriteriu(deschis.criteriu))
    : "";

  return (
    <>
      <Dialog open={deschis !== null} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="flex max-h-[92vh] w-[96vw] max-w-[1500px] flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle className="font-heading pr-8">{titlu}</DialogTitle>
            <DialogDescription>
              {firme.data ? (
                <>
                  {formatNumar(firme.data.total)} firme
                  {firme.data.trunchiat &&
                    `, afișate primele ${formatNumar(firme.data.limita)}`}
                  {firme.data.neincadrate > 0 &&
                    `; alte ${formatNumar(firme.data.neincadrate)} firme active au criteriul, dar nu sunt în tabelul de încadrare`}
                  . Raportul are aceleași filtre, coloane și exporturi ca lista
                  de firme.
                </>
              ) : (
                "Se încarcă firmele…"
              )}
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={doarScop}
              onCheckedChange={(v) => setDoarScop(v === true)}
            />
            Doar firmele în scop (mari, mijlocii, aproape de prag și cele din
            platformă)
          </label>
          {firme.error && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              Firmele nu au putut fi încărcate: {firme.error.message}
            </p>
          )}
          <div className="min-h-0 flex-1 overflow-auto">
            <RaportInteractiv
              definitie={definitieModal}
              randuri={randuri}
              isLoading={firme.isLoading}
              selectat={cuiSelectat}
              onSelect={(cui) => setCuiSelectat(cui)}
              celula={celulaFirma}
              claseRand={(r) => (r.insolventa ? "opacity-70" : undefined)}
              actiuniSelectie={[
                {
                  eticheta: "Adaugă selecția la ținte",
                  onClick: (cuis) => setTinteDeAdaugat(cuis),
                },
              ]}
            />
          </div>
        </DialogContent>
      </Dialog>
      <FisaFirmaDialog cui={cuiSelectat} onClose={() => setCuiSelectat(null)} />
      <AdaugaTinteDialog
        deschis={tinteDeAdaugat !== null}
        onClose={() => setTinteDeAdaugat(null)}
        cuiInitiale={tinteDeAdaugat ?? []}
        campanii={tinte.data?.campanii ?? []}
        sursa="criteriu"
      />
    </>
  );
}
